import { useEffect, useMemo } from 'react';
import { getCourseTree, PHRASES_COURSE_ID, type CourseTree } from '../content/course.js';
import { getLesson } from '../content/lessons.js';
import { usePhrasesContent, type PhrasesContentStatus } from '../content/usePhrasesContent.js';
import { preloadAudio } from '../audio/audioPlayer.js';
import { audioKeysOf } from '../lesson/audioKeys.js';
import { useCompletions } from '../progress/hooks.js';
import { isLessonGated } from '../subscription/access.js';
import { useFreeAccess, useProAccessState } from '../subscription/hooks.js';
import { buildPhrasesView, type PhraseCompletion, type PhrasesView } from './progression.js';

/**
 * The ფრაზები screen's data: the phrases course tree + this learner's
 * completions + Pro access, folded into a `PhrasesView` by the pure
 * `buildPhrasesView` (progression.ts).
 */

export interface PhrasesTreeState {
  /** null while the lessons load, after a failed load, or when the phrases
   *  course is not in this build — `content` tells which. */
  tree: CourseTree | null;
  /** The lazy phrases lessons (lessons.ts): mounting starts their download. */
  content: PhrasesContentStatus;
  retry: () => void;
}

/** The phrases course tree. Its lessons are not in the start-up bundle:
 *  this hook fetches them (unless `enabled` is false), and the tree appears
 *  once they are in. */
export function usePhrasesTree(enabled = true): PhrasesTreeState {
  const { status, retry } = usePhrasesContent(enabled);
  // The lessons arrive once; after that the tree never changes at runtime.
  const tree = useMemo(() => (status === 'ready' ? getCourseTree(PHRASES_COURSE_ID) : null), [status]);
  return { tree, content: status, retry };
}

export interface PhrasesViewState {
  /** null = the phrases course is missing (empty state) — or not loaded
   *  yet / failed to load (see `content`). */
  view: PhrasesView | null;
  /** First load: completions are still the empty placeholder, so states
   *  (done / next) are not known yet — render a skeleton, not wrong states. */
  pending: boolean;
  /** The phrases lessons: 'loading' → album skeleton, 'error' → retry. */
  content: PhrasesContentStatus;
  retry: () => void;
}

export function usePhrasesView(): PhrasesViewState {
  const { tree, content, retry } = usePhrasesTree();
  const { data: completions = {}, isPlaceholderData } = useCompletions();
  const access = useFreeAccess();
  const pro = useProAccessState();

  const view = useMemo(() => {
    if (!tree) return null;
    const adapted: Record<string, PhraseCompletion> = {};
    for (const [id, row] of Object.entries(completions)) {
      adapted[id] = { perfect: row.perfect, completedAt: Date.parse(row.completed_at) || 0 };
    }
    // Only a definite 'no' padlocks: while the subscription read is in
    // flight a subscriber must not see Pro badges flash over their lessons.
    return buildPhrasesView(tree, adapted, {
      isProLocked: (lessonId) => pro === 'no' && isLessonGated(access, lessonId),
    });
  }, [tree, completions, access, pro]);

  return { view, pending: isPlaceholderData, content, retry };
}

/** Warm the audio of the lesson the learner will most likely tap next, so it
 *  plays offline too (same CacheFirst path as Home's pre-cache). */
export function usePrecacheLessonAudio(lessonId: string | null): void {
  useEffect(() => {
    if (!lessonId) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    const lesson = getLesson(lessonId);
    if (!lesson) return;
    for (const key of new Set(audioKeysOf(lesson.exercises))) preloadAudio(key);
  }, [lessonId]);
}
